import { useEffect, useRef, useState } from 'react';
import type { Programme } from '@tariff/authoring';
import type { PublishedCell, RateBand } from '@rates/index';
import { compileProgramme } from '@tariff/authoring';
import { compressToBands, materialise, verifyCells } from '@rates/index';
import { useT } from '../lib/i18n';
import { count, money, money0, shortDate, toDay, toIso } from '../lib/format';
import type { MaterialiseMessage, MaterialiseRequest } from '../lib/materialise.worker';

const LENGTHS = [1, 2, 3, 4, 5, 6, 7, 10, 14, 21];

interface Result {
  cells: PublishedCell[];
  bands: RateBand[];
  stats: { total: number; bookable: number; onRequest: number; closed: number; ms: number };
  verified: { checked: number; mismatches: number };
}

/**
 * The market-facing grid.
 *
 * The park prices by SEARCH — a stay is tiled by rates and the guest pays the
 * cheapest legal tiling. No channel consumes that. Booking.com, Airbnb and
 * every channel manager pull ARI: a dense grid of
 * (unit, arrival, length of stay) -> price, on a schedule. This panel is where
 * the search becomes that grid, and where an operator can see exactly what a
 * channel would receive before it is sent.
 */
export function Published({ programme, quantities }: {
  programme: Programme; quantities: Record<string, number>;
}) {
  const t = useT();
  const [from, setFrom] = useState(() => defaultFrom(programme));
  const [days, setDays] = useState(90);
  const [result, setResult] = useState<Result | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const workerRef = useRef<Worker | null>(null);

  useEffect(() => () => workerRef.current?.terminate(), []);

  const run = () => {
    workerRef.current?.terminate();
    setResult(null);
    setError(null);
    setProgress({ done: 0, total: days * LENGTHS.length });

    const start = toDay(from) ?? 0;
    const req: MaterialiseRequest = {
      programme,
      unitType: programme.id,
      from: start,
      until: start + days,
      lengths: LENGTHS,
      quantities,
      source: `${programme.id}@draft`,
    };

    let w: Worker | null = null;
    try {
      w = new Worker(new URL('../lib/materialise.worker.ts', import.meta.url), { type: 'module' });
    } catch {
      // No worker available — a single-file build has nothing to load, and some
      // sandboxes refuse module workers outright. Fall back to the main thread
      // in slices so the page still responds, rather than showing nothing.
      w = null;
    }

    if (!w) { void materialiseOnMainThread(req, setProgress, setResult, setError); return; }

    workerRef.current = w;
    w.onmessage = (ev: MessageEvent<MaterialiseMessage>) => {
      const m = ev.data;
      if (m.type === 'progress') setProgress({ done: m.done, total: m.total });
      else if (m.type === 'error') { setError(m.message); setProgress(null); }
      else {
        setResult({ cells: m.cells, bands: m.bands, stats: m.stats, verified: m.verified });
        setProgress(null);
      }
    };
    w.onerror = () => {
      w?.terminate();
      workerRef.current = null;
      void materialiseOnMainThread(req, setProgress, setResult, setError);
    };
    w.postMessage(req);
  };

  return (
    <div className="stack">
      <section className="panel">
        <div className="panel-hd">
          <h2>{t('published.heading')}</h2>
          <span className="grow" />
          <span className="note">{t('published.note')}</span>
        </div>
        <div className="panel-bd">
          <div className="controls" style={{ marginBottom: 14 }}>
            <div className="field">
              <label htmlFor="pubFrom">{t('published.firstArrival')}</label>
              <input id="pubFrom" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="pubDays">{t('published.days')}</label>
              <input
                id="pubDays" type="number" min={7} max={400} value={days}
                onChange={(e) => setDays(Math.max(7, Math.min(400, Number(e.target.value) || 7)))}
              />
            </div>
            <div className="field">
              <label>{t('published.lengths')}</label>
              <span className="note">{t('published.lengthsValue', { lengths: LENGTHS.join(', ') })}</span>
            </div>
            <div className="field">
              {/*
                No <label> here. A label whose text is a non-breaking space,
                used only to line the control up with its neighbours, becomes
                the button's ACCESSIBLE NAME — so screen readers announced it as
                "\u00a0" and it could not be found by name at all. Spacing is a
                layout job; give it to CSS.
              */}
              <span aria-hidden="true" style={{ height: 17 }} />
              <button type="button" className="btn pri" onClick={run} disabled={progress !== null}>
                {progress ? t('published.materialising') : t('published.materialise')}
              </button>
            </div>
          </div>

          {progress && (
            <div>
              <div className="bar-track">
                <div className="bar-fill" style={{ width: `${(progress.done / Math.max(1, progress.total)) * 100}%` }} />
              </div>
              <p className="note" style={{ marginTop: 6 }}>
                {t('published.progress', { done: progress.done, total: progress.total })}
              </p>
            </div>
          )}

          {error && <p className="note" style={{ color: 'var(--crit-ink)' }}>{error}</p>}

          {result && <Stats result={result} />}
        </div>
      </section>

      {result && (
        <div className="cols">
          <section className="panel">
            <div className="panel-hd">
              <h2>{t('published.cellsHeading')}</h2>
              <span className="grow" />
              <span className="chip">{t('published.rowCount', { count: result.cells.length })}</span>
            </div>
            <div className="panel-bd">
              <p className="note" style={{ marginBottom: 10 }}>{t('published.cellsNote')}</p>
              <div className="hm">
                <table>
                  <thead>
                    <tr>
                      <th>{t('published.columnArrival')}</th>
                      <th className="r">{t('published.columnLos')}</th>
                      <th className="r">{t('published.columnPrice')}</th>
                      <th>{t('published.columnStatus')}</th>
                      <th>{t('published.columnSource')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.cells.slice(0, 400).map((c) => (
                      <tr key={`${c.date}-${c.los}`}>
                        <td className="num">{shortDate(c.date)}</td>
                        <td className="r">{c.los}</td>
                        <td className="r">{c.price != null ? money(c.price) : '—'}</td>
                        {/* Status and source are the payload itself — what a
                            channel would receive, byte for byte. Translating
                            them would show something that is not what is sent. */}
                        <td>{c.status}</td>
                        <td className="mono" style={{ fontSize: 11 }}>{c.source}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {result.cells.length > 400 && (
                <p className="note" style={{ marginTop: 8 }}>
                  {t('published.truncated', { total: result.cells.length })}
                </p>
              )}
            </div>
          </section>

          <section className="panel">
            <div className="panel-hd">
              <h2>{t('published.bandsHeading')}</h2>
              <span className="grow" />
              <span className="chip">{t('published.bandCount', { count: result.bands.length })}</span>
            </div>
            <div className="panel-bd">
              {/* Bands are the authoring shape the market itself uses
                  (Lodgify, Beds24, NextPax), which is why storage matches it.
                  The collapse is lossless: expanding reproduces every cell. */}
              <p className="note" style={{ marginBottom: 10 }}>{t('published.bandsNote')}</p>
              <div className="hm">
                <table>
                  <thead>
                    <tr>
                      <th>{t('published.columnFrom')}</th>
                      <th>{t('published.columnTo')}</th>
                      <th className="r">{t('published.columnLos')}</th>
                      <th className="r">{t('published.columnPrice')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.bands.slice(0, 400).map((b, i) => (
                      <tr key={`${b.los}-${b.from}-${i}`}>
                        <td className="num">{shortDate(b.from)}</td>
                        <td className="num">{shortDate(b.to)}</td>
                        <td className="r">{b.los}</td>
                        <td className="r">{b.price != null ? money(b.price) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}

function Stats({ result }: { result: Result }) {
  const t = useT();
  const { stats, bands, cells, verified } = result;
  const ratio = cells.length ? (bands.length / cells.length) * 100 : 0;
  const drift = verified.mismatches > 0;
  const prices = cells.filter((c) => c.price != null).map((c) => c.price!);
  const lowest = prices.length ? Math.min(...prices) : null;

  return (
    <section className="tiles" style={{ marginTop: 4 }}>
      <div className="tile lead">
        <span className="k">{t('published.statCells')}</span>
        <span className="v">{count(stats.total)}</span>
        <span className="n">
          {t('published.statCellsNote', { bookable: stats.bookable, closed: stats.closed })}
        </span>
      </div>
      <div className="tile">
        <span className="k">{t('published.statStoredAs')}</span>
        <span className="v">{bands.length}</span>
        <span className="n">{t('published.statStoredAsNote', { percent: ratio.toFixed(0) })}</span>
      </div>
      <div className="tile">
        <span className="k">{t('published.statVerified')}</span>
        <span className="v" style={{ fontSize: 17, paddingTop: 6 }}>
          {drift
            ? <span className="pill bad">{t('published.statDrifted', { count: verified.mismatches })}</span>
            : <span className="pill ok">{t('published.statNoDrift', { count: verified.checked })}</span>}
        </span>
        <span className="n">{t('published.statVerifiedNote')}</span>
      </div>
      <div className="tile">
        <span className="k">{t('published.statCheapest')}</span>
        <span className="v">{lowest != null ? money0(lowest) : '—'}</span>
        <span className="n">
          {t('published.statCheapestNote', { seconds: (stats.ms / 1000).toFixed(1) })}
        </span>
      </div>
    </section>
  );
}

/**
 * The same work, on this thread, yielding between slices.
 *
 * Slower and it does compete with rendering, but a page that cannot show a rate
 * grid at all is worse than one that takes a few seconds to. The arithmetic is
 * identical — the same `materialise`, the same verification — so the result is
 * not a lesser one.
 */
async function materialiseOnMainThread(
  req: MaterialiseRequest,
  setProgress: (p: { done: number; total: number } | null) => void,
  setResult: (r: Result) => void,
  setError: (e: string) => void,
): Promise<void> {
  try {
    const { set } = compileProgramme(req.programme);
    const lengths = req.lengths;
    const total = (req.until - req.from) * lengths.length;
    const cells: PublishedCell[] = [];
    const t0 = Date.now();
    const stats = { total: 0, bookable: 0, onRequest: 0, closed: 0, ms: 0 };

    for (let date = req.from; date < req.until; date += 7) {
      const until = Math.min(req.until, date + 7);
      const slice = materialise(set, {
        unitType: req.unitType, from: date, until, lengths,
        quantities: req.quantities, source: req.source,
      });
      cells.push(...slice.cells);
      stats.total += slice.stats.total;
      stats.bookable += slice.stats.bookable;
      stats.onRequest += slice.stats.onRequest;
      stats.closed += slice.stats.closed;
      setProgress({ done: cells.length, total });
      // Hand the frame back so the progress bar actually moves.
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => { requestAnimationFrame(() => r(null)); });
    }
    stats.ms = Date.now() - t0;
    const v = verifyCells(set, cells, req.quantities, 400);
    setResult({
      cells,
      bands: compressToBands(cells),
      stats,
      verified: { checked: v.checked, mismatches: v.mismatches.length },
    });
    setProgress(null);
  } catch (err) {
    setError((err as Error).message);
    setProgress(null);
  }
}

function defaultFrom(p: Programme): string {
  const d = toDay(p.defaultArrival ?? '');
  if (d !== null) return toIso(d);
  let lo: number | null = null;
  for (const r of p.rates) {
    const f = toDay(r.active.from);
    if (f !== null && (lo === null || f < lo)) lo = f;
  }
  return toIso(lo ?? Math.round(Date.now() / 86_400_000));
}
